import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Legend
} from 'recharts';
import { Settings } from 'lucide-react';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';

/**
 * Une répartition par dimension, pour Piano Analytics et Matomo.
 *
 * Sert les **Appareils**, **Navigateurs**, **Langues**, **Jours de la semaine** et **Pages** :
 * tous sont le même tableau à une dimension près — un camembert des visites, un camembert des
 * transactions, un tableau trié, une ligne de totaux.
 *
 * Il attend le **vocabulaire commun** — `visits`, `visitors`, `pageViews`, `bounceRate`,
 * `transactions`… — que chaque service produit à la lecture. Les écrans Canaux et Géographie,
 * plus anciens, gardent chacun le leur ; c'est pourquoi ils ne passent pas par ici.
 *
 * ## Pourquoi un composant partagé, là où GA4 en a deux
 *
 * `GA4DevicesChart` et `GA4BrowsersChart` sont deux copies du même code, et le second porte même
 * en commentaire qu'il « réutilise ga4_devices pour l'instant ». Le défaut corrigé aujourd'hui
 * sur la durée moyenne aurait demandé deux correctifs au lieu d'un.
 *
 * ## Pourquoi ne pas réemployer celui de GA4
 *
 * Trois obstacles :
 *
 * 1. Il **écrit son identifiant en dur** (`'ga4_devices'`) dans cinq appels — réglage des
 *    métriques, objectifs, modale de configuration. Réemployé ici, Piano hériterait des réglages
 *    de Google Analytics et les écraserait.
 * 2. Il lit `sessions` et `users` ; Piano compte des `visits` et des `visitors`.
 * 3. Il écrit ses intitulés de colonnes en dur, quand les composants Piano les prennent dans la
 *    table commune (`getMetricLabel`) — c'est ce qui permet de renommer une colonne une fois pour
 *    toute l'application.
 *
 * Il suit donc le modèle de `PianoChannelsChart`, son voisin immédiat : mêmes classes, mêmes
 * camemberts, mêmes colonnes e-commerce, même réglage des métriques.
 *
 * ## Les lignes arrivent déjà normalisées
 *
 * Piano nomme ses appareils « Mobile Phone » et ses navigateurs par version (« Chrome 152.x »).
 * La traduction et le regroupement se font **au service**, une seule fois, plutôt qu'ici : le
 * panneau d'analyse IA lit les mêmes données et doit y trouver « Mobile », pas « Mobile Phone ».
 */


/*
 * ─── Les colonnes, décrites une fois ───────────────────────────────────────────────────────
 *
 * **Le tableau rendait ses colonnes dans un ordre figé**, écrit en dur trois fois — en-têtes,
 * cellules, totaux. Choisir le modèle « E-commerce » réglait bien *quelles* métriques afficher,
 * mais pas leur ordre : la configuration portait une suite, l'écran en imposait une autre, et
 * les colonnes marchandes restaient reléguées derrière le taux de rebond.
 *
 * Elles sont donc décrites ici, et le rendu parcourt `componentMetrics.order` — la suite choisie
 * par le modèle. C'est ce que fait déjà `GA4DevicesChart`, et ce que les trois séquences
 * recopiées empêchaient.
 *
 * `total` à `null` marque ce qui ne s'additionne pas : un taux et un panier moyen se recalculent
 * sur les sommes, et ces recalculs vivent dans `totaux`.
 */
const COLONNES = {
  visites: { champ: 'visits', format: (v, f) => f(v), variation: true },
  visiteurs: { champ: 'visitors', format: (v, f) => f(v), variation: true },
  rebond: { champ: 'bounceRate', format: (v, f) => `${f(v, 1)}%`, variation: true, inverse: true },
  pages_vues: { champ: 'pageViews', format: (v, f) => f(v), variation: true },
  taux_conv: { champ: 'conversionRate', format: (v, f) => `${f(v, 2)}%` },
  conv: { champ: 'conversions', format: (v, f) => f(v), variation: true },
  valeur_conv: { champ: 'conversionsValue', format: (v, f) => `${f(v, 2)}\u20ac` },
  duree_moy: { champ: 'avgTimeOnSite', duree: true },
  panier_moyen: { champ: 'avgOrderValue', format: (v, f) => `${f(v, 2)}\u20ac` },
  /*
   * ⚠️ **Prix moyen n'est pas panier moyen.** L'un divise le chiffre d'affaires par la
   * quantité, l'autre par les commandes : sur un palmarès de produits, seul le premier se
   * recoupe avec ce que la ligne affiche. 98 boîtes vendues 2 303 € en 39 commandes font
   * 23,50 € de prix moyen et 59,05 € de « panier » — deux nombres justes, une seule question.
   */
  prix_moyen: { champ: 'avgUnitPrice', format: (v, f) => `${f(v, 2)}\u20ac` },
  // Les quatre colonnes e-commerce, communes aux trois solutions de mesure.
  pages_visite: { champ: 'actionsPerVisit', format: (v, f) => f(v, 2) },
  transactions: { champ: 'transactions', format: (v, f) => f(v, 0), variation: true },
  /*
   * ⚠️ **Les quantités ne sont pas les commandes**, et c'est tout l'intérêt sur un classement
   * de produits : trente-trois exemplaires vendus en trente-trois commandes et trente-trois
   * vendus en cinq ne décrivent pas le même produit. « Commandes » y répondait à une question
   * que personne ne pose devant un palmarès de ventes.
   */
  quantite: { champ: 'quantity', format: (v, f) => f(v, 0), variation: true },
  chiffre_affaires: { champ: 'totalRevenue', format: (v, f) => `${f(v, 2)}\u20ac`, variation: true },
  taux_transaction: { champ: 'transactionRate', format: (v, f) => `${f(v, 2)}%` }
};

const COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#00C49F', '#FF6B6B', '#4ECDC4'];

/**
 * Le mauve de la **seconde série** — celle de la valeur, par opposition au volume.
 *
 * Exporté parce qu'un autre écran s'y réfère : l'histogramme de « Ventes de la boutique » doit
 * porter la même couleur que le chiffre d'affaires d'« États de commande », les deux parlant de
 * la même grandeur. Recopier le code hexadécimal les ferait diverger au premier ajustement, et
 * la divergence ne se verrait qu'en comparant deux blocs côte à côte.
 */
export const COULEUR_SERIE_VALEUR = '#8884d8';

const RepartitionChart = ({
  /** Les lignes à ventiler, et celles de la période précédente. */
  lignes: lignesBrutes = [],
  lignesPrecedentes = [],
  /** Le champ qui identifie une ligne — `device`, `browser`… — et celui qui l'intitule. */
  cleDimension = 'device',
  cleLibelle = 'deviceLabel',
  /** Ce qui s'affiche : titre de section, en-tête de la première colonne, nom du camembert. */
  titre = 'Appareils',
  intituleColonne = 'Appareil',
  nomAuSingulier = 'appareil',
  /**
   * Le tri à l'ouverture. `key: null` conserve l'ordre d'arrivée — c'est ce qu'il faut pour les
   * jours de la semaine, qu'un classement par volume rendrait illisibles : personne ne lit
   * « samedi, dimanche, mercredi, vendredi » comme une semaine.
   */
  triInitial = { key: 'visits', direction: 'desc' },
  /**
   * La mesure de volume du bloc : celle qui décide qu'une ligne a quelque chose à dire, celle
   * que porte le camembert de gauche et la barre de gauche.
   *
   * ⚠️ Elle valait `visits` **en dur**, à cinq endroits. Une boutique PrestaShop ne compte pas
   * de visite : ses lignes étaient donc toutes écartées par le filtre ci-dessous, et les deux
   * blocs — états de commande, meilleures ventes — se rendaient **vides**, sans un message.
   * Constaté à l'écran le 29 octobre 2026, alors que la route servait bien ses 6 états et ses
   * 12 produits. Un bloc vide se lit comme « rien à montrer », pas comme un défaut.
   */
  mesurePrincipale = { cle: 'visites', champ: 'visits' },
  /**
   * La seconde série, quand le choix automatique ne convient pas.
   *
   * Par défaut le bloc prend les transactions, à défaut les conversions — le bon choix pour une
   * solution de mesure. Chez un marchand dont les transactions sont déjà la mesure principale,
   * ce serait tracer deux fois la même série : la vue passe alors son chiffre d'affaires.
   */
  mesureSecondaire = null,
  /**
   * La forme du bloc graphique : `'camemberts'`, `'camembert'`, `'barres'`, ou `'aucun'`.
   *
   * Un camembert répond à « quelle part ? », une barre à « combien ? ». Les sept jours d'une
   * semaine n'ont pas de part à montrer — ils se comparent les uns aux autres, et c'est
   * exactement ce que fait le composant Jours de la semaine de Google Analytics : deux séries
   * de barres sur deux axes, volume à gauche, conversions à droite.
   *
   * **`'aucun'` n'est pas un repli, c'est un choix.** Une dimension à longue traîne — cinquante
   * pages, dont la première pèse un cinquième et les dernières un millième — ne se lit pas en
   * parts : le camembert y devient un disque plein cerné de filets. Le tableau, lui, répond.
   *
   * **`'camembert'` au singulier** retire le second graphique, pour les sources qui n'ont pas de
   * mesure marchande sur cette dimension. Le proposer vide inviterait à activer une colonne qui
   * resterait à zéro : Matomo n'archive aucun objectif par langue, et aucun réglage n'y changera
   * quelque chose.
   */
  graphe = 'camemberts',
  compareEnabled = false,
  formatNumber,
  loading = false,
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  componentId = 'piano_devices',
  moduleId = 'piano_devices',
  /**
   * Le nombre de visiteurs du site sur la période, quand on le connaît.
   *
   * **Les visiteurs uniques ne s'additionnent pas.** Chaque rapport Matomo rend, par ligne, la
   * somme de ses uniques quotidiens : un visiteur venu par deux canaux compte deux fois, et le
   * pied de tableau annonçait 7 836 là où les appareils en donnaient 7 075 et la géographie
   * 6 909. Trois totaux pour la même grandeur, sur le même écran.
   *
   * Le pied affiche donc le compte du site — celui de la carte Utilisateurs — plutôt qu'une
   * somme qui n'a pas de sens. Les lignes gardent leur valeur : elles ne s'additionnent pas, et
   * c'est vrai de tous les outils de mesure.
   */
  visiteursDuSite = null,
  /** Le logo de la solution de mesure, affiché devant le titre. */
  logo = null,
  /**
   * Une icône, qui l'emporte sur le logo.
   *
   * Les écrans Google Analytics identifient leur sujet par une icône — un moniteur pour les
   * appareils, un calendrier pour les jours — et non par le logo de la solution, répété à
   * l'identique huit fois sur la page. Les composants Matomo suivent cette convention ; ceux de
   * Piano gardent leur logo, qui est leur signe distinctif sur un dashboard où il est seul.
   */
  icone: Icone = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState(triInitial);
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const componentMetrics = useComponentMetrics(componentId, accountId);

  const isMetricActive = useCallback(
    (metricKey) => componentMetrics.metrics.includes(metricKey),
    [componentMetrics.metrics]
  );

  const libelleColonne = (cle) => getMetricLabel(cle, componentId, accountId);

  /*
   * Les colonnes à rendre, **dans l'ordre du modèle**.
   *
   * `componentMetrics.order` porte la suite choisie ; `metrics` dit lesquelles sont actives. On
   * suit la première et on filtre par la seconde, en écartant ce que ce composant ne sait pas
   * rendre — une clé inconnue de `COLONNES` viendrait d'un modèle conçu pour une autre famille.
   */
  const colonnes = useMemo(() => {
    const ordre = componentMetrics.order?.length ? componentMetrics.order : componentMetrics.metrics;
    return (ordre || [])
      .filter((cle) => COLONNES[cle] && isMetricActive(cle))
      .map((cle) => ({ cle, ...COLONNES[cle] }));
  }, [componentMetrics.order, componentMetrics.metrics, isMetricActive]);

  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  /*
   * Les lignes sans volume sont écartées.
   *
   * Piano rend tous ses types quoi qu'il arrive : un compte purement mobile affichait donc
   * « Téléviseur 0 » et « Console 0 », des lignes vides noyant celle qui porte le trafic.
   *
   * ⚠️ Le volume est celui de `mesurePrincipale`, et non `visits`. Voir l'en-tête de la prop :
   * câblé sur les visites, ce filtre vidait entièrement un bloc dont les lignes n'en portent
   * pas.
   */
  const lignes = useMemo(
    () => (lignesBrutes || []).filter((ligne) => (ligne?.[mesurePrincipale.champ] || 0) > 0),
    [lignesBrutes, mesurePrincipale.champ]
  );

  const lignesTriees = useMemo(() => {
    // Aucune colonne de tri : l'ordre d'arrivée fait foi.
    if (!sortConfig.key) return lignes;
    const copie = [...lignes];
    copie.sort((a, b) => {
      const va = sortConfig.key === cleLibelle ? (a[cleLibelle] || '') : (a[sortConfig.key] || 0);
      const vb = sortConfig.key === cleLibelle ? (b[cleLibelle] || '') : (b[sortConfig.key] || 0);
      if (typeof va === 'string') {
        return sortConfig.direction === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return sortConfig.direction === 'asc' ? va - vb : vb - va;
    });
    return copie;
  }, [lignes, sortConfig, cleLibelle]);

  /*
   * Dix lignes à l'écran, le reste à la page suivante.
   *
   * Un site marchand rend cinquante pages et trente-deux navigateurs : la table occupait trois
   * écrans pour une traîne que personne ne lit. Le compteur dit combien il en reste, de sorte
   * qu'un classement tronqué ne se prenne pas pour un classement complet.
   */
  const totalPages = Math.ceil(lignesTriees.length / ITEMS_PER_PAGE);
  const lignesAffichees = useMemo(() => {
    const debut = (currentPage - 1) * ITEMS_PER_PAGE;
    return lignesTriees.slice(debut, debut + ITEMS_PER_PAGE);
  }, [lignesTriees, currentPage]);

  // Changer de tri remet au début : rester en page 3 d'un autre classement n'a pas de sens.
  useEffect(() => { setCurrentPage(1); }, [sortConfig, lignes]);

  /*
   * Les totaux se somment, **les taux se recalculent**.
   *
   * Moyenner les taux de rebond donnerait autant de poids aux neuf visites d'un ordinateur
   * qu'aux quatre-vingt-dix mille d'un mobile — mesuré sur un compte réel.
   */
  const totaux = useMemo(() => {
    const cumul = lignes.reduce((acc, l) => ({
      visits: acc.visits + (l.visits || 0),
      visitors: acc.visitors + (l.visitors || 0),
      pageViews: acc.pageViews + (l.pageViews || 0),
      rebondPondere: acc.rebondPondere + ((l.bounceRate || 0) * (l.visits || 0)),
      dureePonderee: acc.dureePonderee + ((l.avgTimeOnSite || 0) * (l.visits || 0)),
      conversions: acc.conversions + (l.conversions || 0),
      conversionsValue: acc.conversionsValue + (l.conversionsValue || 0),
      transactions: acc.transactions + (l.transactions || 0),
      totalRevenue: acc.totalRevenue + (l.totalRevenue || 0),
      // ⚠️ Une colonne du catalogue absente d'ici rend un pied de tableau vide et un camembert
      // à 0 % partout : le total sert aussi de dénominateur aux parts.
      quantity: acc.quantity + (l.quantity || 0)
    }), {
      visits: 0, visitors: 0, pageViews: 0, rebondPondere: 0, dureePonderee: 0,
      conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0, quantity: 0
    });

    return {
      ...cumul,
      bounceRate: cumul.visits > 0 ? cumul.rebondPondere / cumul.visits : 0,
      avgTimeOnSite: cumul.visits > 0 ? cumul.dureePonderee / cumul.visits : 0,
      conversionRate: cumul.visits > 0 ? (cumul.conversions / cumul.visits * 100) : 0,
      transactionRate: cumul.visits > 0 ? (cumul.transactions / cumul.visits * 100) : 0,
      actionsPerVisit: cumul.visits > 0 ? cumul.pageViews / cumul.visits : 0,
      avgOrderValue: cumul.transactions > 0 ? cumul.totalRevenue / cumul.transactions : 0,
      // ⚠️ Recalculé sur les cumuls, comme les autres moyennes : sommer des prix moyens
      // donnerait le même poids à un produit vendu une fois qu'à un autre vendu cent fois.
      avgUnitPrice: cumul.quantity > 0 ? cumul.totalRevenue / cumul.quantity : 0
    };
  }, [lignes]);

  const totauxPrecedents = useMemo(() => {
    if (!compareEnabled || !lignesPrecedentes?.length) return null;
    const cumul = lignesPrecedentes.reduce((acc, l) => ({
      visits: acc.visits + (l.visits || 0),
      visitors: acc.visitors + (l.visitors || 0),
      pageViews: acc.pageViews + (l.pageViews || 0),
      conversions: acc.conversions + (l.conversions || 0),
      conversionsValue: acc.conversionsValue + (l.conversionsValue || 0)
    }), { visits: 0, visitors: 0, pageViews: 0, conversions: 0, conversionsValue: 0 });
    return cumul;
  }, [compareEnabled, lignesPrecedentes]);

  /*
   * Couleur figée sur l'ordre d'arrivée, pas sur l'ordre d'affichage : trier le tableau ne doit
   * pas repeindre le camembert, sans quoi la légende ne correspond plus à rien.
   */
  const couleurParLigne = useMemo(() => {
    const map = {};
    lignes.forEach((ligne, index) => {
      map[ligne[cleLibelle]] = COLORS[index % COLORS.length];
    });
    return map;
  }, [lignes, cleLibelle]);

  /**
   * Ce que le second camembert représente.
   *
   * **Les transactions l'emportent quand elles sont affichées** — c'est le choix d'un compte
   * marchand, et le modèle « E-commerce » les active. À défaut, les conversions. Si aucune des
   * deux n'est à l'écran, le graphique le dit plutôt que d'afficher un cadre vide.
   *
   * Même règle que `PianoChannelsChart` : le camembert et le tableau doivent annoncer la même
   * chose, faute de quoi ils se contredisent sans que rien ne le signale.
   */
  const mesureDuCamembert = useMemo(() => {
    // Une vue qui sait ce qu'elle veut l'emporte sur la déduction.
    if (mesureSecondaire) return mesureSecondaire;
    // ⚠️ Et jamais la mesure principale : tracer deux fois la même série n'apprend rien.
    const candidats = [
      { cle: 'transactions', champ: 'transactions' },
      { cle: 'conv', champ: 'conversions' }
    ].filter((c) => c.cle !== mesurePrincipale.cle);
    return candidats.find((c) => isMetricActive(c.cle)) || null;
  }, [isMetricActive, mesureSecondaire, mesurePrincipale.cle]);

  const totalDuCamembert = mesureDuCamembert ? (totaux[mesureDuCamembert.champ] || 0) : 0;

  const donneesCamembertMesure = useMemo(() => {
    if (!mesureDuCamembert || totalDuCamembert <= 0) return [];
    return lignes
      .filter((ligne) => (ligne[mesureDuCamembert.champ] || 0) > 0)
      .map((ligne) => ({
        name: ligne[cleLibelle],
        value: ligne[mesureDuCamembert.champ] || 0,
        percentage: ((ligne[mesureDuCamembert.champ] || 0) / totalDuCamembert * 100).toFixed(1)
      }));
  }, [lignes, mesureDuCamembert, totalDuCamembert, cleLibelle]);

  const totalPrincipal = totaux[mesurePrincipale.champ] || 0;
  const donneesCamembert = useMemo(() => lignes.map((ligne) => ({
    name: ligne[cleLibelle],
    value: ligne[mesurePrincipale.champ] || 0,
    percentage: totalPrincipal > 0
      ? ((ligne[mesurePrincipale.champ] || 0) / totalPrincipal * 100).toFixed(1)
      : '0.0'
  })), [lignes, totalPrincipal, cleLibelle, mesurePrincipale.champ]);

  /*
   * Dix étiquettes au plus. Un site marchand rend cinquante pages et trente-deux navigateurs :
   * la colonne du milieu s'étirait sur plusieurs écrans et désignait des parts invisibles à
   * l'œil. Le camembert et les totaux, eux, portent toujours sur toutes les lignes.
   */
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(lignes);

  /** L'intitulé de la seconde série : celui de la mesure retenue, ou rien si aucune n'est active. */
  const libelleMesureSecondaire = mesureDuCamembert ? libelleColonne(mesureDuCamembert.cle) : '';

  /*
   * Le graphe à barres lit l'ordre d'arrivée, jamais le tri du tableau : trier par chiffre
   * d'affaires ne doit pas réordonner les jours de la semaine sous le graphique.
   */
  const donneesBarres = useMemo(() => lignes.map((ligne) => ({
    libelle: ligne[cleLibelle],
    principale: ligne[mesurePrincipale.champ] || 0,
    secondaire: mesureDuCamembert ? (ligne[mesureDuCamembert.champ] || 0) : 0
  })), [lignes, cleLibelle, mesureDuCamembert, mesurePrincipale.champ]);

  /** La durée de Piano est en secondes : on l'écrit en minutes:secondes, comme chez GA4. */
  const formaterDuree = (secondes) => {
    const minutes = Math.floor((secondes || 0) / 60);
    const reste = Math.floor((secondes || 0) % 60);
    return `${minutes}:${reste.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <>
        <div className="section-header">
          <h4>
            {Icone
              ? <Icone size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              : logo && (
                <img
                  src={logo}
                  alt=""
                  style={{ width: '18px', height: '18px', marginRight: '8px', verticalAlign: 'middle', borderRadius: '4px' }}
                />
              )}
            {titre}
          </h4>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des donnees...
        </div>
      </>
    );
  }

  // Un écran sans ligne ne se distingue pas d'un module en panne : on ne l'affiche pas,
  // exactement comme les canaux et la géographie.
  if (!lignes.length) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ fontSize: '18px', fontWeight: '600' }}>
          {Icone
            ? <Icone size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            : logo && (
              <img
                src={logo}
                alt=""
                style={{ width: '18px', height: '18px', marginRight: '8px', verticalAlign: 'middle', borderRadius: '4px' }}
              />
            )}
          {titre}
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title={titre}
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? 'Masquer la section' : 'Afficher la section'}
            >
              {isVisible ? '-' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {graphe === 'aucun' ? null : graphe === 'barres' ? (
            /*
             * Le même graphe que le composant Jours de la semaine de Google Analytics
             * (`WeekdaysTable`) : deux séries de barres sur deux axes — volume à gauche, mesure
             * marchande à droite —, parce que les ordres de grandeur n'ont rien de comparable et
             * qu'un axe unique écraserait la seconde série contre zéro.
             */
            <div style={{
              width: '100%', height: '400px', background: 'white', borderRadius: '12px',
              padding: '20px', boxSizing: 'border-box', overflow: 'hidden', marginBottom: '20px'
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={donneesBarres} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="libelle" stroke="#6b7280" tick={{ fontSize: 12 }} height={40} />
                  <YAxis
                    yAxisId="left"
                    stroke="#82ca9d"
                    tick={{ fontSize: 12 }}
                    label={{ value: libelleColonne(mesurePrincipale.cle), angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke={COULEUR_SERIE_VALEUR}
                    tick={{ fontSize: 12 }}
                    label={{ value: libelleMesureSecondaire, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    cursor={{ fill: '#f9fafb' }}
                    formatter={(valeur) => formatNumber(valeur)}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="principale"
                    fill="#82ca9d"
                    fillOpacity={0.7}
                    name={libelleColonne(mesurePrincipale.cle)}
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  />
                  {mesureDuCamembert && (
                    <Bar
                      yAxisId="right"
                      dataKey="secondaire"
                      fill={COULEUR_SERIE_VALEUR}
                      fillOpacity={0.8}
                      name={libelleMesureSecondaire}
                      radius={[4, 4, 0, 0]}
                      isAnimationActive={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
          <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{libelleColonne(mesurePrincipale.cle)} par {nomAuSingulier}</h5>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={donneesCamembert}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percentage }) => `${percentage}%`}
                      outerRadius={70}
                      dataKey="value"
                      isAnimationActive={false}
                    >
                      {donneesCamembert.map((part, index) => (
                        <Cell key={`part-${index}`} fill={couleurParLigne[part.name] || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${formatNumber(value)} ${libelleColonne(mesurePrincipale.cle).toLowerCase()}`, name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div style={{
                minWidth: '150px',
                padding: '0 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                {legendeVisible.map((ligne, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', fontSize: '13px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: couleurParLigne[ligne[cleLibelle]],
                      marginRight: '8px',
                      borderRadius: '2px',
                      flexShrink: 0
                    }}></div>
                    <span>{ligne[cleLibelle]}</span>
                  </div>
                ))}
                {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                {legendeReste > 0 && (
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                    + {legendeReste} autres
                  </div>
                )}
              </div>

              {/* Second camembert : transactions, ou conversions à défaut */}
              {graphe !== 'camembert' && (
              <div style={{ flex: 1, textAlign: 'center' }}>
                <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>
                  {mesureDuCamembert ? `${libelleColonne(mesureDuCamembert.cle)} par ${nomAuSingulier}` : `Transactions par ${nomAuSingulier}`}
                </h5>
                {totalDuCamembert > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={donneesCamembertMesure}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={70}
                        dataKey="value"
                        isAnimationActive={false}
                      >
                        {donneesCamembertMesure.map((part, index) => (
                          <Cell
                            key={`mesure-${index}`}
                            fill={couleurParLigne[part.name] || COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`${formatNumber(value)} ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                    {/* Un cadre vide et sans titre se lit comme une panne : on dit pourquoi. */}
                    {mesureDuCamembert
                      ? `Aucune ${libelleColonne(mesureDuCamembert.cle).toLowerCase()}`
                      : 'Activez Conversions ou Transactions pour ce graphique'}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          )}

          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table className="devices-table" style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th onClick={() => handleSort(cleLibelle)} className="sortable">
                      {intituleColonne} {sortConfig.key === cleLibelle && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {colonnes.map((colonne) => (
                      <th
                        key={colonne.cle}
                        onClick={() => handleSort(colonne.champ)}
                        className="sortable numeric"
                      >
                        {libelleColonne(colonne.cle)} {sortConfig.key === colonne.champ && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {lignesAffichees.map((ligne, index) => {
                    /*
                     * L'appariement se fait sur la clé normalisée, et non sur le libellé :
                     * un libellé traduit changerait et la variation disparaîtrait sans bruit,
                     * ce qui se lirait comme une absence de comparaison.
                     */
                    const precedent = compareEnabled && lignesPrecedentes
                      ? lignesPrecedentes.find((p) => p[cleDimension] === ligne[cleDimension])
                      : null;

                    return (
                      <tr key={ligne[cleDimension] || index}>
                        <td className="device-name" style={{ whiteSpace: 'nowrap' }}>
                          <span style={{
                            display: 'inline-block',
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: couleurParLigne[ligne[cleLibelle]]
                              || COLORS[((currentPage - 1) * ITEMS_PER_PAGE + index) % COLORS.length],
                            marginRight: '8px',
                            flexShrink: 0
                          }}></span>
                          {ligne[cleLibelle]}
                        </td>
                        {colonnes.map((colonne) => (
                          <td key={colonne.cle} className="numeric">
                            {colonne.duree
                              ? formaterDuree(ligne[colonne.champ])
                              : colonne.format(ligne[colonne.champ] || 0, formatNumber)}
                            {colonne.variation && precedent && renderVariation(
                              calculateVariation(ligne[colonne.champ] || 0, precedent[colonne.champ] || 0),
                              colonne.inverse
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr className="total-row">
                    <td><strong>TOTAL</strong></td>
                    {colonnes.map((colonne) => (
                      <td
                        key={colonne.cle}
                        className="numeric"
                        title={colonne.cle === 'visiteurs' && visiteursDuSite !== null
                          ? 'Visiteurs du site sur la période : les lignes ne s\'additionnent pas, un visiteur pouvant apparaître dans plusieurs.'
                          : undefined}
                      >
                        <strong>
                          {colonne.cle === 'visiteurs' && visiteursDuSite !== null
                            ? formatNumber(visiteursDuSite)
                            : colonne.duree
                              ? formaterDuree(totaux[colonne.champ])
                              : colonne.format(totaux[colonne.champ] || 0, formatNumber)}
                        </strong>
                        {/*
                          Pas de variation sur un total qu'on ne calcule pas ici : la comparer à
                          une somme de lignes opposerait deux grandeurs différentes.
                        */}
                        {colonne.variation
                          && !(colonne.cle === 'visiteurs' && visiteursDuSite !== null)
                          && totauxPrecedents && renderVariation(
                            calculateVariation(totaux[colonne.champ] || 0, totauxPrecedents[colonne.champ] || 0),
                            colonne.inverse
                          )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-info">
                    Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, lignesTriees.length)} sur {lignesTriees.length} {nomAuSingulier}s
                  </div>
                  <div className="pagination-buttons">
                    <button
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                      className="pagination-btn"
                    >
                      Precedent
                    </button>
                    <button
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={currentPage === totalPages}
                      className="pagination-btn"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showMetricsConfig && (
        <MetricsConfigModal
          isOpen={showMetricsConfig}
          onClose={() => setShowMetricsConfig(false)}
          componentId={componentId}
          accountId={accountId}
          componentTitle={titre}
        />
      )}
    </>
  );
};

export default RepartitionChart;
